import { useCallback, useState } from "react";
import { lumi } from "@/lib/lumi";
import type {
  CandidateQualification,
  CandidateMobility,
  CandidateHousingNeed,
  AvailableCandidate,
} from "@/hooks/useAvailableCandidates";

/**
 * Hook de gestion des alertes "Candidat disponible immédiatement" côté recruteur.
 * Critères simplifiés : Diplôme, Fréquence, Région.
 * À la connexion d'un recruteur, on filtre les nouveaux profils par diplôme et région,
 * puis on notifie le recruteur par email selon la fréquence choisie
 * (immédiate, quotidienne, hebdomadaire).
 */

export type CandidateAlertQualification = CandidateQualification | "ALL";
export type CandidateAlertFrequency = "immediate" | "daily" | "weekly";
/** Conservés pour compat ascendante avec d'anciennes alertes en base. */
export type CandidateAlertMobility = "any" | CandidateMobility;
export type CandidateAlertHousing = "any" | "non" | "oui";

export interface CandidateAlert {
  _id: string;
  creator: string;
  creatorEmail?: string;
  name: string;
  qualification: CandidateAlertQualification;
  /** Région française. Vide = toutes les régions. */
  region?: string;
  frequency: CandidateAlertFrequency;
  isActive: boolean;
  lastSentAt?: string;
  lastNotifiedCandidateIds?: string[];
  createdAt: string;
  updatedAt: string;
  /** Champs hérités, conservés pour compat (non exposés dans l'UI). */
  department?: string;
  city?: string;
  mobility?: CandidateAlertMobility;
  housingNeed?: CandidateAlertHousing;
  specialty?: string;
}

export interface NewCandidateAlert {
  name: string;
  qualification: CandidateAlertQualification;
  region?: string;
  frequency: CandidateAlertFrequency;
  isActive: boolean;
  /** Champs hérités, conservés pour compat. */
  department?: string;
  city?: string;
  mobility?: CandidateAlertMobility;
  housingNeed?: CandidateAlertHousing;
  specialty?: string;
}

const COLLECTION = "candidate_alerts";

export function useCandidateAlerts() {
  const [alerts, setAlerts] = useState<CandidateAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Charge les alertes du recruteur connecté */
  const fetchMine = useCallback(async (userId: string) => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const { list } = await lumi.entities[COLLECTION].list({
        filter: { creator: userId },
        sort: { createdAt: -1 },
      });
      setAlerts((list as CandidateAlert[]) || []);
    } catch (e) {
      console.error("[useCandidateAlerts] fetchMine error", e);
      setError("Impossible de charger vos alertes candidats.");
    } finally {
      setLoading(false);
    }
  }, []);

  /** Crée une nouvelle alerte */
  const createAlert = useCallback(
    async (
      userId: string,
      userEmail: string | undefined,
      data: NewCandidateAlert
    ): Promise<CandidateAlert | null> => {
      if (!userId) return null;
      const now = new Date().toISOString();
      try {
        const created = (await lumi.entities[COLLECTION].create({
          ...data,
          creator: userId,
          creatorEmail: userEmail || "",
          lastNotifiedCandidateIds: [],
          createdAt: now,
          updatedAt: now,
        })) as CandidateAlert;
        setAlerts((prev) => [created, ...prev]);
        return created;
      } catch (e) {
        console.error("[useCandidateAlerts] createAlert error", e);
        throw e;
      }
    },
    []
  );

  /** Met à jour une alerte */
  const updateAlert = useCallback(
    async (
      alertId: string,
      data: Partial<NewCandidateAlert> & {
        lastSentAt?: string;
        lastNotifiedCandidateIds?: string[];
      }
    ) => {
      const now = new Date().toISOString();
      try {
        const updated = (await lumi.entities[COLLECTION].update(alertId, {
          ...data,
          updatedAt: now,
        })) as CandidateAlert;
        setAlerts((prev) =>
          prev.map((a) => (a._id === alertId ? { ...a, ...updated } : a))
        );
        return updated;
      } catch (e) {
        console.error("[useCandidateAlerts] updateAlert error", e);
        throw e;
      }
    },
    []
  );

  /** Supprime une alerte */
  const deleteAlert = useCallback(async (alertId: string) => {
    try {
      await lumi.entities[COLLECTION].delete(alertId);
      setAlerts((prev) => prev.filter((a) => a._id !== alertId));
    } catch (e) {
      console.error("[useCandidateAlerts] deleteAlert error", e);
      throw e;
    }
  }, []);

  return {
    alerts,
    loading,
    error,
    fetchMine,
    createAlert,
    updateAlert,
    deleteAlert,
  };
}

/**
 * Vérifie si un profil candidat correspond à une alerte recruteur.
 * Critères simplifiés :
 *  - qualification : "ALL" matche tout, sinon égalité stricte ;
 *  - region : recherche partielle insensible à la casse sur la région du candidat.
 *
 * Pour compat ascendante, les anciens champs (`department`, `city`, `mobility`,
 * `housingNeed`, `specialty`) restent évalués si présents sur l'alerte mais
 * de manière non bloquante quand la donnée candidat est vide.
 */
export function candidateMatchesAlert(
  candidate: Pick<
    AvailableCandidate,
    | "qualification"
    | "city"
    | "region"
    | "mobility"
    | "housingNeed"
  > & { specialty?: string; mobilityDepartment?: string },
  alert: Pick<
    CandidateAlert,
    | "qualification"
    | "region"
    | "department"
    | "city"
    | "mobility"
    | "housingNeed"
    | "specialty"
  >
): boolean {
  // Qualification
  if (alert.qualification && alert.qualification !== "ALL") {
    if (candidate.qualification !== alert.qualification) return false;
  }
  // Région (critère principal)
  if (alert.region && alert.region.trim()) {
    const needle = alert.region.trim().toLowerCase();
    const haystack = (candidate.region || "").toLowerCase();
    if (!haystack.includes(needle)) return false;
  }

  // --- Compat ascendante (alertes créées avec l'ancien modèle) ---
  if (alert.department && alert.department.trim()) {
    const needle = alert.department.trim().toLowerCase();
    const explicit = (candidate.mobilityDepartment || "").toString().toLowerCase();
    const fallback = `${candidate.city || ""} ${candidate.region || ""}`.toLowerCase();
    const haystack = `${explicit} ${fallback}`.trim();
    if (haystack.length > 0 && !haystack.includes(needle)) return false;
  }
  if (alert.city && alert.city.trim()) {
    const needle = alert.city.trim().toLowerCase();
    const haystack = (candidate.city || "").toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  if (alert.mobility && alert.mobility !== "any") {
    const min = MOBILITY_RANK[alert.mobility as CandidateMobility];
    const cur = MOBILITY_RANK[candidate.mobility];
    if (typeof min === "number" && typeof cur === "number" && cur < min) {
      return false;
    }
  }
  if (alert.housingNeed && alert.housingNeed !== "any") {
    const need = candidate.housingNeed;
    if (alert.housingNeed === "non" && need !== "non") return false;
    if (alert.housingNeed === "oui" && need !== "oui" && need !== "oui_si_eloigne") {
      return false;
    }
  }
  if (alert.specialty && alert.specialty.trim()) {
    const candidateSpecialty = (candidate.specialty || "")
      .toString()
      .trim()
      .toLowerCase();
    if (candidateSpecialty.length > 0) {
      const needle = alert.specialty.trim().toLowerCase();
      if (!candidateSpecialty.includes(needle)) return false;
    }
  }
  return true;
}

/** Ordre de mobilité — utilisé uniquement pour la compat ascendante. */
const MOBILITY_RANK: Record<CandidateMobility, number> = {
  non: 0,
  locale: 1,
  departementale: 2,
  regionale: 3,
  france: 4,
};

export const CANDIDATE_ALERT_QUALIFICATIONS: CandidateAlertQualification[] = [
  "ALL",
  "BNSSA",
  "BPJEPS AAN",
  "BEESAN",
  "STAPS",
];
